import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import { IconType } from 'react-icons';
import SetPageTitle from 'components/Portals/SetPageTitle';
import {
	RiAddCircleLine,
	RiBankCardLine,
	RiFileList2Line,
	RiGlobalLine,
	RiLinksLine,
	RiMapPinTimeLine,
	RiNotification2Line,
	RiPercentLine,
	RiSettings4Line,
	RiStore2Line,
	RiUser3Line,
	RiUserSmileLine,
	RiWindowLine,
} from 'react-icons/ri';
import { Link } from 'react-router-dom';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization';
import { sortBy } from 'lodash';
import PaymentMethodBanner from 'components/PaymentMethodBanner';
import classNames from 'classnames';
import { useHasFeature } from 'hooks/useHasFeature';

interface AccountLandingItem {
	label: string;
	route: string;
	Icon: IconType;
	hidden?: boolean;
	locked?: boolean;
}

const AccountLanding = () => {
	const { t } = useTranslation();
	const { hasFeature } = useHasFeature();
	const classes = useStyles();

	const items: AccountLandingItem[] = [
		{
			route: Routes.ACCOUNT_GENERAL,
			label: t('account.general.pageTitle', 'General'),
			Icon: RiSettings4Line,
			hidden: !hasFeature(['LEGAL_SETTINGS', 'BRANDING'], { filteringType: 'SOME' }),
		},
		{
			route: Routes.ACCOUNT_TERMS,
			label: t('account.termsAndPolicies.pageTitle', 'Terms & Policies'),
			Icon: RiFileList2Line,
			hidden: !hasFeature('LEGAL_SETTINGS'),
		},
		{
			route: Routes.ACCOUNT_STORES,
			label: t('account.stores.pageTitle', 'Stores'),
			Icon: RiStore2Line,
			hidden: !hasFeature('STORES'),
		},
		{
			route: Routes.ACCOUNT_BILLING,
			label: t('account.billing.pageTitle', 'Plan & Billing'),
			Icon: RiFileList2Line,
			hidden: !hasFeature('PLAN_BILLING'),
		},
		{
			route: Routes.ACCOUNT_PAYMENTS,
			label: t('account.payments.pageTitle', 'Payments'),
			Icon: RiBankCardLine,
			hidden: !hasFeature('PAYMENT_SETTINGS'),
		},
		{
			route: Routes.ACCOUNT_INTEGRATIONS,
			label: t('account.integrations.pageTitle', 'Integrations'),
			Icon: RiLinksLine,
			hidden: !hasFeature('INTEGRATIONS'),
		},
		{
			route: Routes.ACCOUNT_ACCOUNTING,
			label: t('account.accounting.pageTitle', 'Accounting'),
			Icon: RiFileList2Line,
			hidden: !hasFeature('REPORTS'),
		},
		{
			route: Routes.ACCOUNT_REPORTS,
			label: t('account.reports.pageTitle', 'Reports'),
			Icon: RiFileList2Line,
			hidden: !hasFeature('REPORTS'),
		},
		{
			route: Routes.ACCOUNT_LOCALIZATION,
			label: t('account.regionalFormats.pageTitle', 'Regional formats'),
			Icon: RiMapPinTimeLine,
			hidden: !hasFeature('LOCALIZATION'),
		},
		{
			route: Routes.ACCOUNT_NOTIFICATIONS,
			label: t('account.notifications.pageTitle', 'Notifications'),
			Icon: RiNotification2Line,
			hidden: !hasFeature('NOTIFICATIONS'),
		},
		{
			route: Routes.ACCOUNT_TAXES,
			label: t('account.taxes.pageTitle', 'Taxes'),
			Icon: RiPercentLine,
			hidden: !hasFeature('TAXES'),
		},
		{
			route: Routes.ACCOUNT_DOMAINS,
			label: t('account.domains.domainsAndUrls', 'Domains & URLs'),
			Icon: RiWindowLine,
			hidden: !hasFeature('DOMAINS'),
		},
		{
			route: Routes.ACCOUNT_PROFILE,
			label: t('account.profile.pageTitle', 'Profile'),
			Icon: RiUserSmileLine,
		},
		{
			route: Routes.ACCOUNT_USERS,
			label: t('account.users.pageTitle', 'Users'),
			Icon: RiUser3Line,
			hidden: !hasFeature('USERS'),
		},
		{
			route: Routes.ACCOUNT_LANGUAGES,
			label: t('account.languages.pageTitle', 'Languages'),
			Icon: RiGlobalLine,
			hidden: !hasFeature('LANGUAGES', { scope: 'user' }),
		},
		{
			route: Routes.ACCOUNT_MARKETING,
			label: t('account.marketing.pageTitle', 'Marketing'),
			Icon: RiFileList2Line,
		},
	];

	const sortedItems = sortBy(items, 'label');

	return (
		<>
			<SetPageTitle priority={1} title={t('account.pageTitle')} />
			<Container>
				<PaymentMethodBanner />
				<ContainerBox>
					<Grid container spacing={2}>
						{sortedItems.map(({ label, Icon, route, hidden, locked }) => {
							if (hidden) return null;
							return (
								<Grid key={route} item xs={12} sm={6} md={4} lg={3} className={classes.gridItem}>
									<Link to={route} className={classes.link}>
										<Box
											className={classNames(classes.linkIcon, {
												[classes.linkIconLocked]: locked,
											})}
										>
											{locked && (
												<Box className={classes.linkIconPlus}>
													<RiAddCircleLine color="inherit" size={18} />
												</Box>
											)}

											<Box display="inline-block" sx={{ opacity: locked ? 0.3 : 1 }}>
												<Icon size={18} />
											</Box>
										</Box>
										<Typography
											variant="h6"
											className={classNames(classes.linkLabel, {
												[classes.linkLabelLocked]: locked,
											})}
										>
											{label}
										</Typography>
									</Link>
								</Grid>
							);
						})}
					</Grid>
				</ContainerBox>
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		link: {
			width: '100%',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			padding: theme.spacing(2),
			borderRadius: 4,
			'&:hover': {
				background: theme.palette.action.hover,
			},
		},

		linkIcon: {
			width: 42,
			height: 42,
			borderRadius: 3,
			border: `1px solid ${theme.palette.border.paper}`,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			color: theme.palette.text.secondary,
			background: theme.palette.background.paper,
			position: 'relative',
		},
		linkIconPlus: {
			color: theme.palette.success.dark,
			position: 'absolute',
			top: -9,
			right: -9,
			background: theme.palette.background.paper,
			borderRadius: '50%',
			width: 20,
			height: 20,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		linkIconLocked: {
			borderColor: 'rgba(0,0,0,0.15)',
		},
		linkLabel: {
			marginLeft: theme.spacing(1.5),
			fontSize: '1.8rem',
		},
		linkLabelLocked: {
			opacity: 0.3,
		},
		gridItem: {
			'&:empty': {
				display: 'none',
			},
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
		},
	}),
);

export default AccountLanding;
